// src/pages/EmployeeRules.tsx (or .jsx)
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from 'components/ui/Header';
import Sidebar from 'components/ui/Sidebar';
import CommandPalette from 'components/ui/CommandPalette';
import {
  useGetEmployeeRulesQuery,
  useAddEmployeeRuleMutation,
  useDeleteEmployeeRuleMutation,
  useUpdateEmployeeRuleMutation,
  useMarkRuleAsReadMutation,
} from 'redux/features/employeeRulesApi';

import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import useUser from 'hooks/useUser';
import Icon from 'components/AppIcon';
import { toast } from 'react-toastify';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import { useDispatch } from 'react-redux';
import { authApi } from 'redux/features/users/authApi';
import SkeletonWrapper from 'components/ui/SkeletonWrapper';
import UsersListModal from './components/UsersListModal';
import CommonDocumentPreview from './components/CommonDocumentPreview';
import AddRuleModal from './components/AddRuleModal';

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const EmployeeRules = () => {
  const user = useUser();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const { data: rulesData, isLoading } = useGetEmployeeRulesQuery();
  const [addEmployeeRule, { isLoading: isAdding }] = useAddEmployeeRuleMutation();
  const [deleteEmployeeRule, { isLoading: isDeleting }] = useDeleteEmployeeRuleMutation();
  const [updateEmployeeRule, { isLoading: isUpdating }] = useUpdateEmployeeRuleMutation();
  const [markRuleAsRead, { isLoading: isMarkingAsRead }] = useMarkRuleAsReadMutation();
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [unreadUsersModalData, setUnreadUsersModalData] = useState(null);

  const dispatch = useDispatch();
  const toggleCard = (cardId) => {
    setExpandedCardId((prev) => (prev === cardId ? null : cardId));
  };

  const handleSaveRule = async (payload) => {
    try {
      if (editingRule) {
        await updateEmployeeRule({ id: editingRule.id, ...payload }).unwrap();
        toast.success('Employee rule updated successfully!');
      } else {
        await addEmployeeRule(payload).unwrap();
        toast.success('Employee rule added successfully!');
      }
      setIsAddModalOpen(false);
      setEditingRule(null);
    } catch (error) {
      console.error(error?.data?.message);
    }
  };

  const handleDeleteRule = async () => {
    try {
      await deleteEmployeeRule(deleteId).unwrap();
      toast.success('Employee rule deleted successfully!');
      setDeleteId(null);
    } catch (error) {
      console.error(error?.data?.message);
    }
  };

  const handleEditClick = (rule) => {
    setEditingRule(rule);
    setIsAddModalOpen(true);
  };

  const handleMarkAsRead = async (ruleId) => {
    try {
      await markRuleAsRead({ id: ruleId, payload: {} }).unwrap();
      dispatch(authApi.util.invalidateTags(['UserDetails']));
      toast.success('You have successfully read and agreed to this rule.');
    } catch (error) {
      console.error(error?.data?.message || 'Failed to mark rule as read');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-secondary-50">
      <Header />
      <Sidebar />
      <CommandPalette />

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="px-4 sm:px-6 lg:px-8 py-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <motion.h1
                variants={itemVariants}
                className="text-2xl font-semibold text-slate-900 tracking-tight"
              >
                Employee Rules
              </motion.h1>

              <motion.p variants={itemVariants} className="mt-1 text-sm text-slate-600">
                Click on any card to view detailed rules or attachments.
              </motion.p>
            </div>

            {isAdmin && (
              <motion.button
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsAddModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-all"
              >
                <Icon name="Plus" size={18} />
                Add New Rule
              </motion.button>
            )}
          </div>

          {/* Cards grid */}
          <motion.div variants={containerVariants} className="mt-6 gap-4 grid grid-cols-1">
            {isLoading ? (
              // Skeleton placeholders while loading
              [...Array(5)].map((_, i) => (
                <SkeletonWrapper
                  key={i}
                  width="w-full"
                  height="h-[60px]"
                  className="rounded-lg border border-gray-200"
                  isLoading={true}
                />
              ))
            ) : rulesData && rulesData.length > 0 ? (
              rulesData.map((item) => {
                const cardId = `${item.id}-${item.type}`;
                const isExpanded = expandedCardId === cardId;
                const isTextType = item.type?.toLowerCase() === 'text';

                return (
                  <motion.div
                    key={cardId}
                    variants={itemVariants}
                    layout
                    className={`group relative rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow ${unreadUsersModalData?.id === item.id ? 'z-50' : 'z-0'}`}
                  >
                    {/* ===== HEADER ===== */}
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
                      <button
                        type="button"
                        onClick={() => toggleCard(cardId)}
                        className="flex-1 text-left outline-none focus:outline-none"
                      >
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-semibold ">{item.title}</h2>
                          {!isAdmin && item.isRead === false && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 border border-green-300">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                              New
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {isTextType
                            ? `${item.rules?.length || 0} Rule${
                                (item.rules?.length || 0) === 1 ? '' : 's'
                              }`
                            : 'Attachment'}
                        </p>
                      </button>

                      <div className="flex items-center gap-2">
                        {/* ADMIN ACTIONS */}
                        {isAdmin && (
                          <>
                            {/* Unread Users */}
                            <div className="relative">
                              <button
                                type="button"
                                className="p-1.5 rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100 hover:text-primary-700 transition-colors"
                                title="Unread Users"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={() =>
                                  setUnreadUsersModalData(
                                    unreadUsersModalData?.id === item.id ? null : item,
                                  )
                                }
                              >
                                <Icon name="EyeOff" size={16} />
                              </button>

                              <UsersListModal
                                open={unreadUsersModalData?.id === item.id}
                                users={item.unread_users || []}
                                onClose={() => setUnreadUsersModalData(null)}
                                title="Unread Users"
                              />
                            </div>

                            {/* Edit */}
                            <button
                              type="button"
                              className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-colors"
                              title="Edit Rule"
                              onClick={() => handleEditClick(item)}
                            >
                              <Icon name="Edit" size={16} />
                            </button>

                            {/* Delete */}
                            <button
                              type="button"
                              className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 transition-colors disabled:opacity-50"
                              title="Delete Rule"
                              onClick={() => setDeleteId(item.id)}
                              disabled={isDeleting}
                            >
                              {isDeleting && deleteId === item.id ? (
                                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                              ) : (
                                <Icon name="Trash" size={16} />
                              )}
                            </button>
                          </>
                        )}

                        {/* Expand / Collapse */}
                        <button
                          type="button"
                          onClick={() => toggleCard(cardId)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          {isExpanded ? 'Hide' : 'View'}
                          <svg
                            className={`h-4 w-4 transform transition-transform ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M5 8L10 13L15 8"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* ===== EXPANDABLE CONTENT ===== */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: 'easeInOut' }}
                          className="overflow-hidden border-t border-gray-200"
                        >
                          <div className="p-4 text-sm text-gray-700 space-y-4">
                            {/* RULES LIST (TEXT TYPE) */}
                            {isTextType ? (
                              item.rules && item.rules.length > 0 ? (
                                <div className="divide-y divide-gray-200">
                                  {item.rules.map((rule, idx) => (
                                    <div
                                      key={idx}
                                      className={`py-3 flex items-start gap-3 p-2  ${
                                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'
                                      }`}
                                    >
                                      <div className="mt-0.5 flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-700">
                                        <Icon name="Check" size={10} />
                                      </div>
                                      <p className="text-sm text-gray-600 leading-relaxed">
                                        {rule}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center py-8 text-center">
                                  <Icon
                                    name="FileQuestion"
                                    size={24}
                                    className="text-gray-400 mb-2"
                                  />
                                  <p className="text-sm font-medium text-gray-500">
                                    No rules added for this category yet.
                                  </p>
                                </div>
                              )
                            ) : (
                              // DOCUMENT PREVIEW
                              <div className="border border-gray-200 rounded bg-white p-4">
                                <CommonDocumentPreview document={item.document} />
                              </div>
                            )}

                            {/* "I Agree" BUTTON (Non-Admin) */}
                            {!isAdmin && item.isRead === false && (
                              <div className="flex justify-end pt-2">
                                <button
                                  type="button"
                                  onClick={() => handleMarkAsRead(item.id)}
                                  disabled={isMarkingAsRead}
                                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isMarkingAsRead ? (
                                    <ThreeDotLoading size="sm" />
                                  ) : (
                                    <>
                                      <Icon name="CheckCircle" size={18} />
                                      <span>I Agree</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            ) : (
              <motion.div variants={itemVariants} className="text-center py-12">
                <p className="text-gray-500">No employee rules found.</p>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </main>

      <AddRuleModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingRule(null);
        }}
        onSave={handleSaveRule}
        isLoading={isAdding || isUpdating}
        initialData={editingRule}
      />

      <ConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteRule}
        isLoading={isDeleting}
        title="Delete Rule"
        message="Are you sure you want to delete this rule? This action cannot be undone."
      />
    </div>
  );
};

export default EmployeeRules;
